/* eslint-disable @typescript-eslint/no-explicit-any */
import { ValidatedInput } from '@cp-shared-19/frontend-ui';
import { Combobox, ErrorMessage, Fieldset, FormField, Layout } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import React, { useCallback, useState } from 'react';
import { countryCodeMapping } from './country-code';
import { ComboboxOption } from '../../../../../../../types/bronson-react';

type PhoneNumberProps = {
    phoneFieldLabel?: string;
    phoneFieldTooltip?: string;
    phoneFieldName: string;
    phoneFieldTestId?: string;
    phoneFieldPrefixLabel?: string;
    phoneFieldPrefixName: string;
    phoneFieldPrefixTestId?: string;
    hidePrefix?: boolean;
    padding?: boolean;
    isMandatory?: boolean;
    inputId?: string;
    setFormikFieldValue: (field: string, value: string) => void;
};

const optionsFromCountryMapping = (): any => {
    return Object.entries(countryCodeMapping).map((value: [string, string[]]) => ({
        key: value[1][0],
        value: value[0],
    }));
};

const getDefaultValue = (key: string): string => {
    return (
        Object.entries(countryCodeMapping).find((value: [string, string[]]) => {
            return value[1][0] === key;
        })?.[0] || ''
    );
};

export const PhoneInput: React.FC<PhoneNumberProps> = ({
    phoneFieldLabel,
    phoneFieldName,
    phoneFieldTestId,
    phoneFieldTooltip,
    phoneFieldPrefixLabel,
    phoneFieldPrefixName,
    phoneFieldPrefixTestId,
    setFormikFieldValue,
    hidePrefix = true,
    isMandatory,
    inputId,
    padding,
}) => {
    const [prefixField, meta] = useField(phoneFieldPrefixName);

    const defaultState = {
        value: [
            {
                value: getDefaultValue(prefixField.value),
                key: prefixField.value,
            },
        ],
    };

    const [selectedItem, setSelectedItem] = useState<{ value: ComboboxOption[] }>(defaultState);

    const onChange = useCallback((selectedPrefixes: any) => {
        setSelectedItem({ value: [selectedPrefixes[0]] });
        setFormikFieldValue(phoneFieldPrefixName, selectedPrefixes[0]?.key);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Fieldset>
            <Fieldset.Row>
                <Layout>
                    {!hidePrefix && (
                        <Layout.Item default="1/2" s="1/1">
                            <FormField
                                className="c-form-field--no-floating"
                                type="select"
                                errorMessage={
                                    meta.touched &&
                                    meta.error && (
                                        <ErrorMessage testId={'textarea-error-message'}>{meta.error}</ErrorMessage>
                                    )
                                }
                                labelText={phoneFieldPrefixLabel || ''}
                                noFloatingLabel
                            >
                                <Combobox
                                    className="c-form-field--no-floating"
                                    name={phoneFieldPrefixName}
                                    testId={phoneFieldPrefixTestId}
                                    defaultState={defaultState}
                                    value={selectedItem.value}
                                    onChange={onChange}
                                    options={optionsFromCountryMapping()}
                                ></Combobox>
                            </FormField>
                        </Layout.Item>
                    )}
                    <Layout.Item
                        default={`1/${hidePrefix ? '1' : '2'}`}
                        s="1/1"
                        className={hidePrefix && !padding ? 'u-pl-none' : ''}
                    >
                        <ValidatedInput
                            className="c-form-field--no-floating"
                            tooltip={phoneFieldTooltip}
                            label={phoneFieldLabel || ''}
                            name={phoneFieldName}
                            testId={phoneFieldTestId}
                            inputId={inputId}
                            addonText={!hidePrefix ? `+${prefixField.value}` : undefined}
                            reversed={!hidePrefix}
                            type="tel"
                            aria-required={isMandatory}
                            isMandatory={isMandatory}
                            requiredText=" *"
                            autocomplete="tel-local"
                            onBlur={(e) => setFormikFieldValue(phoneFieldName, e.target.value.replace(/ +/g, ''))}
                        />
                    </Layout.Item>
                </Layout>
            </Fieldset.Row>
        </Fieldset>
    );
};
